/**
 * "Continue with …" buttons. Each is a plain link to the API, which sends
 * the browser to the provider and back: a full-page navigation, not a
 * popup, because popups are unreliable inside an installed app on a phone.
 *
 * Only providers the server has keys for are drawn; with none, nothing is.
 * The provider marks are the vendors' own, at their required proportions.
 */
import { useEffect, useState } from 'react';
import { api } from './api.js';

type Provider = { id: 'google' | 'microsoft' | 'facebook'; label: string };

function Mark({ id }: { id: Provider['id'] }) {
  if (id === 'google') {
    return (
      <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
      </svg>
    );
  }
  if (id === 'microsoft') {
    return (
      <svg width="20" height="20" viewBox="0 0 21 21" aria-hidden="true">
        <rect x="1" y="1" width="9" height="9" fill="#F25022" /><rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
        <rect x="1" y="11" width="9" height="9" fill="#00A4EF" /><rect x="11" y="11" width="9" height="9" fill="#FFB900" />
      </svg>
    );
  }
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#1877F2" d="M24 12a12 12 0 1 0-13.88 11.85v-8.38H7.08V12h3.04V9.36c0-3 1.79-4.67 4.53-4.67 1.31 0 2.69.24 2.69.24v2.95h-1.52c-1.49 0-1.96.93-1.96 1.88V12h3.33l-.53 3.47h-2.8v8.38A12 12 0 0 0 24 12z" />
      <path fill="#fff" d="M16.66 15.47 17.19 12h-3.33V9.76c0-.95.47-1.88 1.96-1.88h1.52V4.93s-1.38-.24-2.69-.24c-2.74 0-4.53 1.66-4.53 4.67V12H7.08v3.47h3.04v8.38a12.1 12.1 0 0 0 3.74 0v-8.38h2.8z" />
    </svg>
  );
}

export function SocialButtons() {
  const [providers, setProviders] = useState<Provider[]>([]);
  useEffect(() => {
    let alive = true;
    api.authProviders().then((r) => { if (alive) setProviders(r.providers); }).catch(() => { /* email still works */ });
    return () => { alive = false; };
  }, []);
  if (providers.length === 0) return null;
  return (
    <div className="social">
      {providers.map((p) => (
        <a key={p.id} className="btn secondary social-btn" href={`/api/v1/auth/oauth/${p.id}/start`}>
          <Mark id={p.id} /><span>Continue with {p.label}</span>
        </a>
      ))}
      <p className="small muted">
        By continuing you confirm you’re 18 or older. Surge only learns your name and email — it can’t post or read anything. <a href="/privacy">Privacy policy</a>
      </p>
      <div className="or" role="separator"><span>or use your email</span></div>
    </div>
  );
}

/** Words for the reason a social sign-in came back without signing in. */
export function authErrorText(code: string): string {
  switch (code) {
    case 'cancelled': return 'Sign-in was cancelled. Nothing was changed.';
    case 'expired': return 'That sign-in took too long or didn’t start on this phone. Try again.';
    case 'no_email': return 'That account didn’t share an email address, which Surge needs. Try another way to sign in.';
    case 'email_exists': return 'That email already has a Surge account. Sign in with your password, or the way you signed in before.';
    case 'unavailable': return 'This account can’t sign in right now.';
    default: return 'Couldn’t finish signing in with that account. Try again, or use your email.';
  }
}
